// Speed round and survival. They share everything except what ends them --
// a clock or a life count -- so they share a component rather than a copy.

import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { useAnswer, useGameFeed, useLanguage } from '../../api/hooks'
import type { PracticeCard } from '../../api/types'
import { Button, Card, ErrorText, Input, Spinner } from '../../components/ui'
import { sfx } from '../../lib/sound'

const SPEED_SECONDS = 60
const SURVIVAL_LIVES = 3

export type RushMode = 'speed' | 'survival'

export default function RushGame({ mode }: { mode: RushMode }) {
  const languageId = Number(useParams().id)
  const { data: language } = useLanguage(languageId)
  const { data: feed, isPending, error, refetch } = useGameFeed(languageId, 120)
  const answer = useAnswer()

  const [index, setIndex] = useState(0)
  const [typed, setTyped] = useState('')
  const [score, setScore] = useState(0)
  const [combo, setCombo] = useState(0)
  const [bestCombo, setBestCombo] = useState(0)
  const [lives, setLives] = useState(SURVIVAL_LIVES)
  const [secondsLeft, setSecondsLeft] = useState(SPEED_SECONDS)
  const [flash, setFlash] = useState<'right' | 'wrong' | null>(null)
  const [over, setOver] = useState(false)
  const [lastExpected, setLastExpected] = useState<string | null>(null)

  const inputRef = useRef<HTMLInputElement>(null)
  const card: PracticeCard | undefined = feed?.[index]

  // The countdown, for speed only.
  useEffect(() => {
    if (mode !== 'speed' || over || !feed) return
    if (secondsLeft <= 0) {
      setOver(true)
      sfx.thud()
      return
    }
    const id = setTimeout(() => setSecondsLeft((s) => s - 1), 1000)
    return () => clearTimeout(id)
  }, [mode, over, secondsLeft, feed])

  useEffect(() => {
    if (!over) inputRef.current?.focus()
  }, [index, over])

  // Running out of cards ends the run in either mode.
  useEffect(() => {
    if (feed && index >= feed.length && feed.length > 0) setOver(true)
  }, [feed, index])

  function restart() {
    setIndex(0)
    setTyped('')
    setScore(0)
    setCombo(0)
    setBestCombo(0)
    setLives(SURVIVAL_LIVES)
    setSecondsLeft(SPEED_SECONDS)
    setOver(false)
    setLastExpected(null)
    refetch()
  }

  function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!card || over) return

    const guess = typed
    setTyped('')

    answer.mutate(
      { entry_id: card.entry_id, direction: card.direction, given: guess },
      {
        onSuccess: (result) => {
          if (result.correct) {
            const streak = combo + 1
            setCombo(streak)
            setBestCombo((b) => Math.max(b, streak))
            // The multiplier is the whole point of a streak: 10 points becomes
            // 50 once you are five deep.
            setScore((s) => s + 10 * Math.min(streak, 5))
            setFlash('right')
            setLastExpected(null)
            if (streak >= 3) sfx.combo(streak)
            else sfx.correct()
          } else {
            setCombo(0)
            setFlash('wrong')
            setLastExpected(result.expected)
            sfx.wrong()
            if (mode === 'survival') {
              const remaining = lives - 1
              setLives(remaining)
              if (remaining <= 0) {
                setOver(true)
                sfx.thud()
              }
            }
          }
          setTimeout(() => setFlash(null), 250)
          setIndex((i) => i + 1)
        },
      },
    )
  }

  if (isPending) return <Spinner label="Shuffling" />
  if (error) {
    return (
      <div className="mx-auto max-w-md space-y-4 text-center">
        <ErrorText error={error} />
        <Link to={`/languages/${languageId}/games`}>
          <Button variant="secondary">Back to games</Button>
        </Link>
      </div>
    )
  }

  if (over) {
    return (
      <div className="mx-auto max-w-md space-y-5 text-center">
        <div className="text-5xl">{mode === 'speed' ? '⚡' : '💀'}</div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {mode === 'speed' ? "Time's up" : 'Out of lives'}
        </h1>
        <Card>
          <p className="text-5xl font-semibold tabular-nums">{score}</p>
          <p className="mt-2 text-sm text-muted">
            Best streak {bestCombo}
            {mode === 'speed' && ` · ${SPEED_SECONDS}s`}
          </p>
        </Card>
        <div className="flex justify-center gap-2">
          <Button onClick={restart}>Play again</Button>
          <Link to={`/languages/${languageId}/games`}>
            <Button variant="secondary">Done</Button>
          </Link>
        </div>
      </div>
    )
  }

  if (!card) return <Spinner />

  return (
    <div className="mx-auto max-w-lg space-y-5">
      <div className="flex items-center justify-between text-sm">
        <Link
          to={`/languages/${languageId}/games`}
          className="text-muted hover:text-ink"
        >
          ✕ Quit
        </Link>
        <div className="flex items-center gap-4">
          {combo >= 3 && <span className="font-semibold text-brand">🔥 {combo}</span>}
          {mode === 'survival' ? (
            <span aria-label={`${lives} lives left`}>
              {'❤️'.repeat(lives)}
              <span className="opacity-25">{'❤️'.repeat(SURVIVAL_LIVES - lives)}</span>
            </span>
          ) : (
            <span
              className={`font-mono tabular-nums ${
                secondsLeft <= 10 ? 'text-negative' : 'text-muted'
              }`}
            >
              {secondsLeft}s
            </span>
          )}
          <span className="font-semibold tabular-nums">{score}</span>
        </div>
      </div>

      <Card
        className={`space-y-4 transition-colors ${
          flash === 'right'
            ? 'border-positive'
            : flash === 'wrong'
              ? 'border-negative'
              : ''
        }`}
      >
        <p className="text-center text-xs uppercase tracking-wide text-muted">
          {card.direction === 'produce'
            ? `${language?.source_language ?? 'English'} → ${language?.name ?? ''}`
            : `${language?.name ?? ''} → ${language?.source_language ?? 'English'}`}
        </p>
        <p className="text-center text-3xl font-semibold tracking-tight">
          {card.prompt}
        </p>

        {lastExpected && (
          <p className="text-center text-sm text-negative">
            Last one was <span className="font-semibold">{lastExpected}</span>
          </p>
        )}

        <form onSubmit={submit}>
          <Input
            ref={inputRef}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Type it, press Enter"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            className="text-center text-lg"
          />
        </form>
        <p className="text-center text-xs text-muted">
          Enter to submit. Don't stop to think — that's the game.
        </p>
      </Card>
    </div>
  )
}
